// React Query hooks for the skip-enquiries surface.
//
// Pairs with /api/v1/skip/enquiries/*. The cancel + convert endpoints
// land alongside the standard CRUD; the contact log lives under
// /:enc_id/contacts (per §7.2).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type {
  SkipEnquiry,
  SkipEnquiryContact,
  SkipEnquiryListResponse,
} from '../../../types/api';

interface SkipEnquiriesFilters {
  page?: number;
  limit?: number;
  status?: string;
  is_priority?: boolean;
  q?: string;
}

export function useSkipEnquiries(filters: SkipEnquiriesFilters = {}) {
  return useQuery<SkipEnquiryListResponse>({
    queryKey: queryKeys.skipEnquiries.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const res = await apiClient.get<SkipEnquiryListResponse>(endpoints.skipEnquiries.list, {
        params: filters,
      });
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function useSkipEnquiry(encId: string | null | undefined) {
  return useQuery<{ enquiry: SkipEnquiry }>({
    queryKey: queryKeys.skipEnquiries.detail(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useSkipEnquiry called without enc_id');
      const res = await apiClient.get<{ enquiry: SkipEnquiry }>(
        endpoints.skipEnquiries.detail(encId),
      );
      return res.data;
    },
    enabled: !!encId,
    staleTime: 30_000,
  });
}

export function useCreateSkipEnquiry() {
  const qc = useQueryClient();
  return useMutation<{ enquiry: SkipEnquiry }, Error, Record<string, unknown>>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ enquiry: SkipEnquiry }, Record<string, unknown>>(
        endpoints.skipEnquiries.create,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipEnquiries.all });
    },
  });
}

export function useUpdateSkipEnquiry(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ enquiry: SkipEnquiry }, Error, Record<string, unknown>>({
    mutationFn: async (body) => {
      const res = await apiClient.put<{ enquiry: SkipEnquiry }, Record<string, unknown>>(
        endpoints.skipEnquiries.update(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipEnquiries.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipEnquiries.detail(encId) });
    },
  });
}

export function useCancelSkipEnquiry(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ enquiry: SkipEnquiry }, Error, { reason: string }>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ enquiry: SkipEnquiry }, { reason: string }>(
        endpoints.skipEnquiries.cancel(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipEnquiries.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipEnquiries.detail(encId) });
    },
  });
}

export interface ConvertEnquiryPayload {
  skip_inventory_uuid?: string;
  /** Phase 6E — preferred over skip_inventory_uuid. */
  skip_inventory_enc_id?: string | null;
  driver_uuid?: string;
  delivery_date?: string | null;
  delivery_time?: string | null;
  collection_date?: string | null;
  collection_time?: string | null;
  price?: number | null;
  notes?: string | null;
  internal_notes?: string | null;
  skip_type?: string;
  skip_size_id?: number | null;
  /** enc_id of the bound skip_stock_sizes row. Wins over
   *  skip_size_id on the convert payload. */
  skip_size_enc_id?: string | null;
  job_type?: string;
  /** Phase 6E — site override at convert time. */
  site_enc_id?: string | null;
  job_address_line1?: string | null;
  job_address_line2?: string | null;
  job_city?: string | null;
  job_county?: string | null;
  job_postcode?: string | null;
  payment_status?: 'unpaid' | 'paid' | 'partial';
  payment_method?: string | null;
  is_priority?: boolean;
  priority_notes?: string | null;
}

export interface ConvertEnquiryResult {
  enquiry: SkipEnquiry;
  job: { enc_id: string; uuid: string; job_number: string };
}

export function useConvertSkipEnquiry(encId: string) {
  const qc = useQueryClient();
  return useMutation<ConvertEnquiryResult, Error, ConvertEnquiryPayload>({
    mutationFn: async (body) => {
      const res = await apiClient.post<ConvertEnquiryResult, ConvertEnquiryPayload>(
        endpoints.skipEnquiries.convert(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipEnquiries.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipEnquiries.detail(encId) });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
    },
  });
}

// -- Contact log (§7.2) -----------------------------------------------------

export function useSkipEnquiryContacts(encId: string | null | undefined) {
  return useQuery<{ contacts: SkipEnquiryContact[]; total: number }>({
    queryKey: queryKeys.skipEnquiries.contacts(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useSkipEnquiryContacts called without enc_id');
      const res = await apiClient.get<{ contacts: SkipEnquiryContact[]; total: number }>(
        endpoints.skipEnquiries.contacts(encId),
      );
      return res.data;
    },
    enabled: !!encId,
    staleTime: 30_000,
  });
}

export interface AddContactPayload {
  contact_method: 'phone' | 'email' | 'sms' | 'in_person' | 'other';
  outcome?: 'answered' | 'no_answer' | 'voicemail' | 'emailed' | 'texted' | 'other';
  notes: string;
  follow_up_date?: string;
}

export function useAddSkipEnquiryContact(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ contact: SkipEnquiryContact }, Error, AddContactPayload>({
    mutationFn: async (body) => {
      const res = await apiClient.post<{ contact: SkipEnquiryContact }, AddContactPayload>(
        endpoints.skipEnquiries.contacts(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.skipEnquiries.contacts(encId) });
      void qc.invalidateQueries({ queryKey: queryKeys.skipEnquiries.detail(encId) });
    },
  });
}
